import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Badge,
  Card,
  Alert,
  Modal,
  Form,
  Row,
  Col,
  Pagination,
  InputGroup,
} from 'react-bootstrap';
import { Quiz, adminService } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
// Quiz settings moved to backend validation

interface AdminQuizListProps {
  onQuizSelect: (quiz: Quiz) => void;
}

interface QuizFormData {
  title: string;
  /** English title — required for WITC (bilingual) quizzes. */
  titleEn: string;
  /** Optional subtitle on lobby cards (quiz primary language). */
  theme: string;
  /** Optional English subtitle for WITC lobby cards. */
  themeEn: string;
  difficulty: string;
  questionCount: number;
  imageUrl: string;
  language: string;
  /** Comma- or semicolon-separated tags (e.g. Weaknesses, Kanto). */
  tags: string;
  format: 'MultipleChoice' | 'Witc' | 'Stop' | 'Genius' | 'TileMatch' | 'WordConnect';
  geniusImageAssetsBasePath: string;
  geniusSoundAssetsBasePath: string;
  tileMatchImageAssetsBasePath: string;
  witcImageAssetsBasePath: string;
  /** Subpasta em public/assets/images/ — opcional; MC partilha a mesma árvore que WITC. */
  mcImageAssetsBasePath: string;
  /** MC: opções de resposta como imagens em public/assets/{pasta}/ */
  mcUseImageAnswers: boolean;
  mcAnswerImageAssetsBasePath: string;
  /** Solo home: pin to top + badge (MC + WITC). */
  recommendedSolo: boolean;
  /** Eligible as career unlock reward; lobbies show padlock. */
  isUnlockable: boolean;
  /** Same-quiz prerequisite questions / progression (optional character unlock). */
  characterUnlockEnabled: boolean;
  /** WITC/MC: níveis de dificuldade com cadastro de respostas no editor de perguntas. */
  supportsPlayDifficultyEasy: boolean;
  supportsPlayDifficultyMedium: boolean;
  supportsPlayDifficultyHard: boolean;
}

function optionalTrimmedOrNull(s: string): string | null {
  const t = s.trim();
  return t ? t : null;
}

function parseCommaTags(raw: string): string[] | undefined {
  const parts = raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : undefined;
}

const ADMIN_QUIZ_LIST_PAGE_SIZE = 50;

function isBilingualLobbyFormat(
  format: 'MultipleChoice' | 'Witc' | 'Stop' | 'Genius' | 'TileMatch' | 'WordConnect'
): boolean {
  return (
    format === 'Witc' ||
    format === 'MultipleChoice' ||
    format === 'Stop' ||
    format === 'Genius' ||
    format === 'TileMatch' ||
    format === 'WordConnect'
  );
}

function isMediumOnlySoloFormat(format: QuizFormData['format']): boolean {
  return format === 'Stop' || format === 'Genius' || format === 'TileMatch';
}

const GENIUS_MIN_POOL_ITEMS = 4;
const TILE_MATCH_MIN_POOL_ITEMS = 11;
const WORD_CONNECT_MIN_POOL_ITEMS = 10;

function isGeniusQuizFormatValue(format: string | undefined): boolean {
  return format === 'Genius';
}

function isTileMatchQuizFormatValue(format: string | undefined): boolean {
  return format === 'TileMatch';
}

function isWordConnectQuizFormatValue(format: string | undefined): boolean {
  return format === 'WordConnect';
}

function requiredQuestionCountForQuiz(quiz: Quiz): number {
  if (isGeniusQuizFormatValue(quiz.format)) return GENIUS_MIN_POOL_ITEMS;
  if (isTileMatchQuizFormatValue(quiz.format)) return TILE_MATCH_MIN_POOL_ITEMS;
  if (isWordConnectQuizFormatValue(quiz.format)) return WORD_CONNECT_MIN_POOL_ITEMS;
  return quiz.questionCount;
}

function requiredQuestionCountForForm(format: QuizFormData['format'], questionCount: number): number {
  if (format === 'Genius') return GENIUS_MIN_POOL_ITEMS;
  if (format === 'TileMatch') return TILE_MATCH_MIN_POOL_ITEMS;
  if (format === 'WordConnect') return WORD_CONNECT_MIN_POOL_ITEMS;
  return questionCount;
}

function minQuestionCountForFormValidation(format: QuizFormData['format']): number {
  if (format === 'Genius') return GENIUS_MIN_POOL_ITEMS;
  if (format === 'TileMatch') return TILE_MATCH_MIN_POOL_ITEMS;
  if (format === 'WordConnect') return WORD_CONNECT_MIN_POOL_ITEMS;
  return 10;
}

function usesWitcImageAssetsFolder(format: QuizFormData['format']): boolean {
  return format === 'Witc' || format === 'Stop';
}

function isQuizFormSaveDisabled(fd: QuizFormData): boolean {
  const minQuestions = minQuestionCountForFormValidation(fd.format);
  return (
    !fd.title.trim() ||
    (isBilingualLobbyFormat(fd.format) && !fd.titleEn.trim()) ||
    fd.questionCount < minQuestions ||
    fd.questionCount > 100 ||
    (usesWitcImageAssetsFolder(fd.format) && !fd.witcImageAssetsBasePath.trim()) ||
    (fd.format === 'Genius' &&
      (!fd.geniusImageAssetsBasePath.trim() || !fd.geniusSoundAssetsBasePath.trim())) ||
    (fd.format === 'TileMatch' && !fd.tileMatchImageAssetsBasePath.trim()) ||
    !playDifficultyOk(fd)
  );
}

function playDifficultyOk(fd: QuizFormData): boolean {
  if (!isBilingualLobbyFormat(fd.format)) return true;
  const any =
    fd.supportsPlayDifficultyEasy ||
    fd.supportsPlayDifficultyMedium ||
    fd.supportsPlayDifficultyHard;
  if (!any) return false;
  if (fd.supportsPlayDifficultyHard && !fd.supportsPlayDifficultyMedium) return false;
  return true;
}

/** Padrão ao escolher tipo WITC — só aplica onde o campo ainda está vazio. */
const WITC_DEFAULT_TITLE_PT = 'Quem é esse personagem?';
const WITC_DEFAULT_TITLE_EN = 'Who is this character?';

function applyQuizFormatChange(
  prev: QuizFormData,
  nextFormat: 'MultipleChoice' | 'Witc' | 'Stop' | 'Genius' | 'TileMatch' | 'WordConnect'
): QuizFormData {
  if (nextFormat === 'MultipleChoice') {
    return { ...prev, format: nextFormat, witcImageAssetsBasePath: '' };
  }
  if (nextFormat === 'WordConnect') {
    return {
      ...prev,
      format: 'WordConnect',
      witcImageAssetsBasePath: '',
      mcImageAssetsBasePath: '',
      mcUseImageAnswers: false,
      mcAnswerImageAssetsBasePath: '',
      supportsPlayDifficultyEasy: true,
      supportsPlayDifficultyMedium: true,
      supportsPlayDifficultyHard: true,
      questionCount: WORD_CONNECT_MIN_POOL_ITEMS,
    };
  }
  if (nextFormat === 'TileMatch') {
    return {
      ...prev,
      format: 'TileMatch',
      witcImageAssetsBasePath: '',
      mcImageAssetsBasePath: '',
      mcUseImageAnswers: false,
      mcAnswerImageAssetsBasePath: '',
      supportsPlayDifficultyEasy: false,
      supportsPlayDifficultyMedium: true,
      supportsPlayDifficultyHard: false,
      questionCount: TILE_MATCH_MIN_POOL_ITEMS,
    };
  }
  if (nextFormat === 'Genius') {
    return {
      ...prev,
      format: 'Genius',
      witcImageAssetsBasePath: '',
      mcImageAssetsBasePath: '',
      mcUseImageAnswers: false,
      mcAnswerImageAssetsBasePath: '',
      supportsPlayDifficultyEasy: false,
      supportsPlayDifficultyMedium: true,
      supportsPlayDifficultyHard: false,
      questionCount: GENIUS_MIN_POOL_ITEMS,
    };
  }
  if (nextFormat === 'Stop') {
    return {
      ...prev,
      format: 'Stop',
      mcImageAssetsBasePath: '',
      mcUseImageAnswers: false,
      mcAnswerImageAssetsBasePath: '',
      supportsPlayDifficultyEasy: false,
      supportsPlayDifficultyMedium: true,
      supportsPlayDifficultyHard: false,
    };
  }
  return {
    ...prev,
    format: 'Witc',
    mcImageAssetsBasePath: '',
    mcUseImageAnswers: false,
    mcAnswerImageAssetsBasePath: '',
    title: prev.title.trim() ? prev.title : WITC_DEFAULT_TITLE_PT,
    titleEn: prev.titleEn.trim() ? prev.titleEn : WITC_DEFAULT_TITLE_EN,
  };
}

const AdminQuizList: React.FC<AdminQuizListProps> = ({ onQuizSelect }) => {
  const { t } = useSafeTranslation();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActiveQuizModal, setShowActiveQuizModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [formData, setFormData] = useState<QuizFormData>({
    title: '',
    titleEn: '',
    theme: '',
    themeEn: '',
    difficulty: 'Easy',
    questionCount: 10, // Backend validates min/max
    imageUrl: '',
    language: 'en',
    tags: '',
    format: 'MultipleChoice',
    witcImageAssetsBasePath: '',
    mcImageAssetsBasePath: '',
    mcUseImageAnswers: false,
    mcAnswerImageAssetsBasePath: '',
    geniusImageAssetsBasePath: '',
    geniusSoundAssetsBasePath: '',
    tileMatchImageAssetsBasePath: '',
    recommendedSolo: false,
    isUnlockable: false,
    characterUnlockEnabled: false,
    supportsPlayDifficultyEasy: true,
    supportsPlayDifficultyMedium: true,
    supportsPlayDifficultyHard: true,
  });

  /** Text in the search box (does not hit the API until applied). */
  const [listSearchInput, setListSearchInput] = useState('');
  /** Term sent to the backend (set only on Enter or search button). */
  const [listSearchApplied, setListSearchApplied] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listHasNext, setListHasNext] = useState(false);
  const [listHasPrev, setListHasPrev] = useState(false);

  const loadQuizzes = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminService.getQuizzes({
        page: listPage,
        pageSize: ADMIN_QUIZ_LIST_PAGE_SIZE,
        search: listSearchApplied.trim() || undefined,
      });
      setQuizzes(res.quizzes);
      setListTotalCount(res.totalCount);
      const tp = res.totalCount === 0 ? 1 : Math.max(1, res.totalPages);
      setListTotalPages(tp);
      setListHasNext(res.hasNextPage);
      setListHasPrev(res.hasPreviousPage);
      setLoadError(false);
      setError(null);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [listPage, listSearchApplied]);

  const applyListSearch = useCallback(() => {
    const q = listSearchInput.trim();
    setListSearchApplied(q);
    setListPage(1);
  }, [listSearchInput]);

  useEffect(() => {
    void loadQuizzes();
  }, [loadQuizzes]);

  useEffect(() => {
    if (listTotalPages > 0 && listPage > listTotalPages) {
      setListPage(listTotalPages);
    }
  }, [listTotalPages, listPage]);

  const totalFiltered = listTotalCount;
  const totalPages = listTotalPages;

  const handleCreateQuiz = async () => {
    try {
      const tagList = parseCommaTags(formData.tags);
      await adminService.createQuiz({
        title: formData.title.trim(),
        titleEn: isBilingualLobbyFormat(formData.format) ? formData.titleEn.trim() : null,
        theme: optionalTrimmedOrNull(formData.theme),
        themeEn: isBilingualLobbyFormat(formData.format)
          ? optionalTrimmedOrNull(formData.themeEn)
          : null,
        difficulty: formData.difficulty,
        questionCount: requiredQuestionCountForForm(formData.format, formData.questionCount),
        imageUrl: formData.imageUrl || undefined,
        language: isBilingualLobbyFormat(formData.format) ? 'both' : formData.language,
        ...(tagList ? { tags: tagList } : {}),
        ...(formData.format === 'Witc'
          ? {
              format: 'Witc',
              witcImageAssetsBasePath: formData.witcImageAssetsBasePath.trim(),
            }
          : formData.format === 'Stop'
            ? {
                format: 'Stop',
                witcImageAssetsBasePath: formData.witcImageAssetsBasePath.trim(),
                supportsPlayDifficultyEasy: false,
                supportsPlayDifficultyMedium: true,
                supportsPlayDifficultyHard: false,
              }
            : formData.format === 'Genius'
              ? {
                  format: 'Genius',
                  geniusImageAssetsBasePath: formData.geniusImageAssetsBasePath.trim(),
                  geniusSoundAssetsBasePath: formData.geniusSoundAssetsBasePath.trim(),
                  supportsPlayDifficultyEasy: false,
                  supportsPlayDifficultyMedium: true,
                  supportsPlayDifficultyHard: false,
                }
              : formData.format === 'TileMatch'
                ? {
                    format: 'TileMatch',
                    tileMatchImageAssetsBasePath: formData.tileMatchImageAssetsBasePath.trim(),
                    supportsPlayDifficultyEasy: false,
                    supportsPlayDifficultyMedium: true,
                    supportsPlayDifficultyHard: false,
                  }
                : formData.format === 'WordConnect'
                  ? {
                      format: 'WordConnect',
                      supportsPlayDifficultyEasy: true,
                      supportsPlayDifficultyMedium: true,
                      supportsPlayDifficultyHard: true,
                    }
                  : {
                format: 'MultipleChoice',
                mcImageAssetsBasePath: formData.mcImageAssetsBasePath.trim() || undefined,
                mcUseImageAnswers: formData.mcUseImageAnswers,
                mcAnswerImageAssetsBasePath: formData.mcUseImageAnswers
                  ? formData.mcAnswerImageAssetsBasePath.trim() || undefined
                  : undefined,
              }),
        recommendedSolo: formData.recommendedSolo,
        isUnlockable: formData.isUnlockable,
        characterUnlockEnabled: formData.characterUnlockEnabled,
        ...(isBilingualLobbyFormat(formData.format) && !isMediumOnlySoloFormat(formData.format)
          ? {
              supportsPlayDifficultyEasy: formData.supportsPlayDifficultyEasy,
              supportsPlayDifficultyMedium: formData.supportsPlayDifficultyMedium,
              supportsPlayDifficultyHard: formData.supportsPlayDifficultyHard,
            }
          : {}),
      });
      setShowCreateModal(false);
      setFormData({
        title: '',
        titleEn: '',
        theme: '',
        themeEn: '',
        difficulty: 'Easy',
        questionCount: 10,
        imageUrl: '',
        language: 'en',
        tags: '',
        format: 'MultipleChoice',
        witcImageAssetsBasePath: '',
        mcImageAssetsBasePath: '',
        mcUseImageAnswers: false,
        mcAnswerImageAssetsBasePath: '',
        geniusImageAssetsBasePath: '',
        geniusSoundAssetsBasePath: '',
        tileMatchImageAssetsBasePath: '',
        recommendedSolo: false,
        isUnlockable: false,
        characterUnlockEnabled: false,
        supportsPlayDifficultyEasy: true,
        supportsPlayDifficultyMedium: true,
        supportsPlayDifficultyHard: true,
      });
      await loadQuizzes();
      setError(null);
    } catch {
      setLoadError(false);
      setError('Failed to create quiz');
    }
  };

  const handleEditQuiz = async () => {
    if (!selectedQuiz) return;
    
    try {
      const tagList = parseCommaTags(formData.tags);
      await adminService.updateQuiz(selectedQuiz.id, {
        title: formData.title.trim(),
        titleEn: isBilingualLobbyFormat(formData.format) ? formData.titleEn.trim() : null,
        theme: optionalTrimmedOrNull(formData.theme),
        themeEn: isBilingualLobbyFormat(formData.format)
          ? optionalTrimmedOrNull(formData.themeEn)
          : null,
        difficulty: formData.difficulty,
        questionCount: requiredQuestionCountForForm(formData.format, formData.questionCount),
        imageUrl: formData.imageUrl || undefined,
        language: isBilingualLobbyFormat(formData.format) ? 'both' : formData.language,
        tags: tagList ?? [],
        ...(formData.format === 'Witc'
          ? {
              format: 'Witc',
              witcImageAssetsBasePath: formData.witcImageAssetsBasePath.trim(),
            }
          : formData.format === 'Stop'
            ? {
                format: 'Stop',
                witcImageAssetsBasePath: formData.witcImageAssetsBasePath.trim(),
                supportsPlayDifficultyEasy: false,
                supportsPlayDifficultyMedium: true,
                supportsPlayDifficultyHard: false,
              }
            : formData.format === 'Genius'
              ? {
                  format: 'Genius',
                  geniusImageAssetsBasePath: formData.geniusImageAssetsBasePath.trim(),
                  geniusSoundAssetsBasePath: formData.geniusSoundAssetsBasePath.trim(),
                  supportsPlayDifficultyEasy: false,
                  supportsPlayDifficultyMedium: true,
                  supportsPlayDifficultyHard: false,
                }
              : formData.format === 'TileMatch'
                ? {
                    format: 'TileMatch',
                    tileMatchImageAssetsBasePath: formData.tileMatchImageAssetsBasePath.trim(),
                    supportsPlayDifficultyEasy: false,
                    supportsPlayDifficultyMedium: true,
                    supportsPlayDifficultyHard: false,
                  }
                : formData.format === 'WordConnect'
                  ? {
                      format: 'WordConnect',
                      supportsPlayDifficultyEasy: true,
                      supportsPlayDifficultyMedium: true,
                      supportsPlayDifficultyHard: true,
                    }
                  : {
                format: 'MultipleChoice',
                mcImageAssetsBasePath: formData.mcImageAssetsBasePath.trim() || undefined,
                mcUseImageAnswers: formData.mcUseImageAnswers,
                mcAnswerImageAssetsBasePath: formData.mcUseImageAnswers
                  ? formData.mcAnswerImageAssetsBasePath.trim() || undefined
                  : undefined,
              }),
        recommendedSolo: formData.recommendedSolo,
        isUnlockable: formData.isUnlockable,
        characterUnlockEnabled: formData.characterUnlockEnabled,
        ...(isBilingualLobbyFormat(formData.format) && !isMediumOnlySoloFormat(formData.format)
          ? {
              supportsPlayDifficultyEasy: formData.supportsPlayDifficultyEasy,
              supportsPlayDifficultyMedium: formData.supportsPlayDifficultyMedium,
              supportsPlayDifficultyHard: formData.supportsPlayDifficultyHard,
            }
          : {}),
      });
      setShowEditModal(false);
      setSelectedQuiz(null);
      setFormData({
        title: '',
        titleEn: '',
        theme: '',
        themeEn: '',
        difficulty: 'Easy',
        questionCount: 10,
        imageUrl: '',
        language: 'en',
        tags: '',
        format: 'MultipleChoice',
        witcImageAssetsBasePath: '',
        mcImageAssetsBasePath: '',
        mcUseImageAnswers: false,
        mcAnswerImageAssetsBasePath: '',
        geniusImageAssetsBasePath: '',
        geniusSoundAssetsBasePath: '',
        tileMatchImageAssetsBasePath: '',
        recommendedSolo: false,
        isUnlockable: false,
        characterUnlockEnabled: false,
        supportsPlayDifficultyEasy: true,
        supportsPlayDifficultyMedium: true,
        supportsPlayDifficultyHard: true,
      });
      await loadQuizzes();
      setError(null);
    } catch {
      setLoadError(false);
      setError('Failed to update quiz');
    }
  };

  const handleDeleteQuiz = async () => {
    if (!selectedQuiz) return;
    
    try {
      await adminService.deleteQuiz(selectedQuiz.id);
      setShowDeleteModal(false);
      setSelectedQuiz(null);
      await loadQuizzes();
      setError(null);
    } catch {
      setLoadError(false);
      setError('Failed to delete quiz');
    }
  };

  const handleActivateQuiz = async (quiz: Quiz) => {
    try {
      await adminService.activateQuiz(quiz.id);
      setError(null);
    } catch (err: unknown) {
      setLoadError(false);
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message ===
          'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      if (apiMessage) {
        setError(apiMessage);
      } else {
        try {
          const refreshed = await adminService.getQuizById(quiz.id);
          if (refreshed.activationBlockers?.length) {
            setError(refreshed.activationBlockers.join(' '));
          } else {
            setError(t('admin.activateQuizFailed', 'Failed to activate quiz'));
          }
        } catch {
          setError(t('admin.activateQuizFailed', 'Failed to activate quiz'));
        }
      }
    } finally {
      await loadQuizzes();
    }
  };

  const handleDeactivateQuiz = async (quiz: Quiz) => {
    try {
      await adminService.deactivateQuiz(quiz.id);
      await loadQuizzes();
      setError(null);
    } catch {
      setLoadError(false);
      setError('Failed to deactivate quiz');
    }
  };

  const handleEditClick = (quiz: Quiz) => {
    setSelectedQuiz(quiz);
    setFormData({
      title: quiz.title,
      titleEn: quiz.titleEn?.trim() ?? '',
      theme: quiz.theme?.trim() ?? '',
      themeEn: quiz.themeEn?.trim() ?? '',
      difficulty: quiz.difficulty,
      questionCount: requiredQuestionCountForQuiz(quiz),
      imageUrl: quiz.imageUrl || '',
      language:
        quiz.format === 'Witc' ||
        quiz.format === 'MultipleChoice' ||
        quiz.format === 'WordConnect' ||
        quiz.language === 'both'
          ? 'both'
          : quiz.language || 'en',
      tags: quiz.tags?.length ? quiz.tags.join(', ') : '',
      format:
        quiz.format === 'Witc'
          ? 'Witc'
          : quiz.format === 'Stop'
            ? 'Stop'
            : quiz.format === 'Genius'
              ? 'Genius'
              : quiz.format === 'TileMatch'
                ? 'TileMatch'
                : quiz.format === 'WordConnect'
                  ? 'WordConnect'
                  : 'MultipleChoice',
      witcImageAssetsBasePath: quiz.witcImageAssetsBasePath || '',
      mcImageAssetsBasePath: quiz.mcImageAssetsBasePath || '',
      mcUseImageAnswers: quiz.mcUseImageAnswers === true,
      mcAnswerImageAssetsBasePath: quiz.mcAnswerImageAssetsBasePath || '',
      geniusImageAssetsBasePath: quiz.geniusImageAssetsBasePath || '',
      geniusSoundAssetsBasePath: quiz.geniusSoundAssetsBasePath || '',
      tileMatchImageAssetsBasePath: quiz.tileMatchImageAssetsBasePath || '',
      recommendedSolo: quiz.recommendedSolo === true,
      isUnlockable: quiz.isUnlockable === true,
      characterUnlockEnabled: quiz.characterUnlockEnabled === true,
      supportsPlayDifficultyEasy:
        quiz.format === 'Stop' || quiz.format === 'Genius' || quiz.format === 'TileMatch'
          ? false
          : quiz.supportsPlayDifficultyEasy !== false,
      supportsPlayDifficultyMedium:
        quiz.format === 'Stop' || quiz.format === 'Genius' || quiz.format === 'TileMatch'
          ? true
          : quiz.supportsPlayDifficultyMedium !== false,
      supportsPlayDifficultyHard:
        quiz.format === 'Stop' || quiz.format === 'Genius' || quiz.format === 'TileMatch'
          ? false
          : quiz.supportsPlayDifficultyHard !== false,
    });

    // Check if quiz is active (Finalized status)
    if (quiz.status === 'Finalized') {
      setShowActiveQuizModal(true);
    } else {
      setShowEditModal(true);
    }
  };

  const handleConfirmEditActiveQuiz = async () => {
    if (!selectedQuiz) return;
    
    try {
      // Set quiz to draft first
      await adminService.setToDraft(selectedQuiz.id);
      setShowActiveQuizModal(false);
      setShowEditModal(true);
      await loadQuizzes(); // Reload to get updated status
      setError(null);
    } catch {
      setLoadError(false);
      setError('Failed to set quiz to draft');
      setShowActiveQuizModal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'New':
        return <Badge bg="secondary">New</Badge>;
      case 'Draft':
        return <Badge bg="warning">Draft</Badge>;
      case 'Finalized':
        return <Badge bg="success">Active</Badge>;
      case 'Disabled':
        return <Badge bg="danger">Disabled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return <Badge bg="success">Easy</Badge>;
      case 'Medium':
        return <Badge bg="warning">Medium</Badge>;
      case 'Hard':
        return <Badge bg="danger">Hard</Badge>;
      default:
        return <Badge bg="secondary">{difficulty}</Badge>;
    }
  };

  const canActivateQuiz = (quiz: Quiz) => {
    const required = requiredQuestionCountForQuiz(quiz);
    return quiz.status === 'Draft' && quiz.totalQuestions >= required && quiz.canActivate;
  };

  const getActivateButtonTooltip = (quiz: Quiz) => {
    if (quiz.status !== 'Draft') {
      return 'Quiz must be in Draft status to activate';
    }
    const required = requiredQuestionCountForQuiz(quiz);
    if (quiz.totalQuestions < required) {
      return isGeniusQuizFormatValue(quiz.format)
        ? `Need ${required - quiz.totalQuestions} more pool items (${quiz.totalQuestions}/${required})`
        : `Need ${required - quiz.totalQuestions} more questions (${quiz.totalQuestions}/${required})`;
    }
    if (!quiz.canActivate && quiz.activationBlockers?.length) {
      return quiz.activationBlockers.join(' ');
    }
    if (!quiz.canActivate) {
      return t(
        'admin.quizCannotActivateGeneric',
        'Cannot activate: check play difficulties, bilingual fields, and correct-answer flags per mode.'
      );
    }
    return t('admin.activateQuiz', 'Activate Quiz');
  };

  const listRangeFrom =
    totalFiltered === 0 ? 0 : (listPage - 1) * ADMIN_QUIZ_LIST_PAGE_SIZE + 1;
  const listRangeTo = Math.min(listPage * ADMIN_QUIZ_LIST_PAGE_SIZE, totalFiltered);
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Quiz Management</h3>
        <Button variant="primary" onClick={() => {
          setFormData({
            title: '',
            titleEn: '',
            theme: '',
            themeEn: '',
            difficulty: 'Easy',
            questionCount: 10,
            imageUrl: '',
            language: 'en',
            tags: '',
            format: 'MultipleChoice',
            witcImageAssetsBasePath: '',
            mcImageAssetsBasePath: '',
            mcUseImageAnswers: false,
            mcAnswerImageAssetsBasePath: '',
            geniusImageAssetsBasePath: '',
            geniusSoundAssetsBasePath: '',
            tileMatchImageAssetsBasePath: '',
            recommendedSolo: false,
            isUnlockable: false,
            characterUnlockEnabled: false,
            supportsPlayDifficultyEasy: true,
            supportsPlayDifficultyMedium: true,
            supportsPlayDifficultyHard: true,
          });
          setShowCreateModal(true);
        }}>
          <i className="bi bi-plus-circle me-2"></i>
          Create New Quiz
        </Button>
      </div>

      {(loadError || error) && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => {
            setLoadError(false);
            setError(null);
          }}
        >
          {error ?? t('quiz.loadQuizzesError', 'Failed to load quizzes.')}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : listTotalCount > 0 || listSearchApplied.trim() ? (
            <>
              <Row className="g-2 mb-3 align-items-end">
                <Col xs={12} md={6} lg={5}>
                  <Form.Group className="mb-0">
                    <Form.Label className="small text-muted mb-1">
                      {t('admin.quizListSearch', 'Search')}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="search"
                        value={listSearchInput}
                        onChange={(e) => setListSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyListSearch();
                          }
                        }}
                        placeholder={t(
                          'admin.quizListSearchPlaceholder',
                          'Filter by title, theme, tags, status, difficulty…'
                        )}
                        autoComplete="off"
                        aria-describedby="admin-quiz-list-search-hint"
                      />
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={applyListSearch}
                        title={t('admin.quizListSearchRun', 'Buscar')}
                        aria-label={t('admin.quizListSearchRun', 'Buscar')}
                      >
                        <i className="bi bi-search" aria-hidden />
                      </Button>
                    </InputGroup>
                    <Form.Text id="admin-quiz-list-search-hint" className="text-muted">
                      {t(
                        'admin.quizListSearchSubmitHint',
                        'Pressione Enter ou o botão de busca para filtrar.'
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={7} className="text-md-end pt-1">
                  <span className="small text-muted">
                    {totalFiltered === 0
                      ? t('admin.quizListShowingEmpty', 'No results')
                      : t('admin.quizListShowing', 'Showing {{from}}–{{to}} of {{total}}')
                          .replace('{{from}}', String(listRangeFrom))
                          .replace('{{to}}', String(listRangeTo))
                          .replace('{{total}}', String(totalFiltered))}
                  </span>
                </Col>
              </Row>
              {listTotalCount === 0 ? (
                <p className="text-muted mb-0">
                  {t('admin.quizListNoMatches', 'No quizzes match your search.')}
                </p>
              ) : (
                <>
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Tags</th>
                        <th>Difficulty</th>
                        <th>Questions</th>
                        <th>Status</th>
                        <th className="text-center text-nowrap">
                          {t('admin.soloRecommendedCol', 'Quiz rec.')}
                        </th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quizzes.map((quiz) => (
                  <tr key={quiz.id}>
                    <td>
                      <strong>{quiz.title}</strong>
                      {(quiz.format === 'Witc' ||
                        quiz.format === 'Stop' ||
                        quiz.format === 'MultipleChoice' ||
                        quiz.format === 'WordConnect' ||
                        quiz.format === 'Genius' ||
                        quiz.format === 'TileMatch') &&
                        quiz.titleEn?.trim() && (
                        <div className="text-muted small">{quiz.titleEn.trim()}</div>
                      )}
                      {quiz.theme?.trim() ? (
                        <div className="text-muted small">
                          <span className="me-1 text-secondary">Tema:</span>
                          {quiz.theme.trim()}
                        </div>
                      ) : null}
                      {(quiz.format === 'Witc' ||
                        quiz.format === 'Stop' ||
                        quiz.format === 'MultipleChoice' ||
                        quiz.format === 'WordConnect' ||
                        quiz.format === 'Genius' ||
                        quiz.format === 'TileMatch') &&
                      quiz.themeEn?.trim() ? (
                        <div className="text-muted small">
                          <span className="me-1 text-secondary">Theme:</span>
                          {quiz.themeEn.trim()}
                        </div>
                      ) : null}
                      {quiz.imageUrl && (
                        <div className="text-muted small">
                          <i className="bi bi-image me-1"></i>
                          Has image
                        </div>
                      )}
                    </td>
                    <td>
                      {quiz.tags && quiz.tags.length > 0 ? (
                        <span className="small">{quiz.tags.join(', ')}</span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td>{getDifficultyBadge(quiz.difficulty)}</td>
                    <td>
                      {(() => {
                        const required = requiredQuestionCountForQuiz(quiz);
                        return (
                          <>
                            {quiz.totalQuestions} / {required}
                            {isGeniusQuizFormatValue(quiz.format) ? (
                              <span className="text-muted small ms-1">(pool)</span>
                            ) : null}
                            {quiz.totalQuestions >= required ? (
                              <i className="bi bi-check-circle text-success ms-2"></i>
                            ) : (
                              <i
                                className="bi bi-exclamation-circle text-warning ms-2"
                                title={
                                  isGeniusQuizFormatValue(quiz.format)
                                    ? `Need ${required - quiz.totalQuestions} more pool items`
                                    : `Need ${required - quiz.totalQuestions} more questions`
                                }
                              ></i>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>{getStatusBadge(quiz.status)}</td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="switch"
                        id={`admin-rec-solo-${quiz.id}`}
                        className="d-inline-block m-0"
                        checked={quiz.recommendedSolo === true}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          try {
                            await adminService.setQuizRecommendedSolo(quiz.id, checked);
                            setQuizzes((prev) =>
                              prev.map((q) =>
                                q.id === quiz.id ? { ...q, recommendedSolo: checked } : q
                              )
                            );
                            setError(null);
                          } catch {
                            setError(t('admin.recommendedSoloUpdateError', 'Failed to update quiz recommendation.'));
                          }
                        }}
                        title={t('admin.soloRecommendedHint', 'Recommended for quiz play — pinned to top of the list')}
                        aria-label={t('admin.soloRecommendedHint', 'Recommended for quiz play — pinned to top of the list')}
                      />
                    </td>
                    <td>{new Date(quiz.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onQuizSelect(quiz)}
                          title="Manage Questions"
                        >
                          <i className="bi bi-list-check"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => handleEditClick(quiz)}
                          title="Edit Quiz"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        
                        {quiz.status === 'Draft' && (
                          canActivateQuiz(quiz) ? (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => handleActivateQuiz(quiz)}
                              title={getActivateButtonTooltip(quiz)}
                            >
                              <i className="bi bi-play-circle"></i>
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled
                              title={getActivateButtonTooltip(quiz)}
                            >
                              <i className="bi bi-play-circle"></i>
                            </Button>
                          )
                        )}
                        
                        {quiz.status === 'Finalized' && (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => handleDeactivateQuiz(quiz)}
                            title="Deactivate Quiz"
                          >
                            <i className="bi bi-pause-circle"></i>
                          </Button>
                        )}
                        
                        {quiz.status === 'Disabled' && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleActivateQuiz(quiz)}
                            title="Reactivate Quiz"
                          >
                            <i className="bi bi-play-circle"></i>
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setSelectedQuiz(quiz);
                            setShowDeleteModal(true);
                          }}
                          title="Delete Quiz"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                      ))}
                    </tbody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination className="mb-0 flex-wrap justify-content-center">
                        <Pagination.First
                          disabled={!listHasPrev}
                          onClick={() => setListPage(1)}
                        />
                        <Pagination.Prev
                          disabled={!listHasPrev}
                          onClick={() => setListPage((p) => Math.max(1, p - 1))}
                        />
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum =
                            Math.max(1, Math.min(totalPages - 4, listPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === listPage}
                              onClick={() => setListPage(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        <Pagination.Next
                          disabled={!listHasNext}
                          onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                        />
                        <Pagination.Last
                          disabled={!listHasNext}
                          onClick={() => setListPage(totalPages)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">No quizzes found. Create your first quiz to get started.</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create Quiz Modal */}
      <Modal show={showCreateModal} onHide={() => {
        setShowCreateModal(false);
        setFormData({
          title: '',
          titleEn: '',
          theme: '',
          themeEn: '',
          difficulty: 'Easy',
          questionCount: 10,
          imageUrl: '',
          language: 'en',
          tags: '',
          format: 'MultipleChoice',
          witcImageAssetsBasePath: '',
          mcImageAssetsBasePath: '',
          mcUseImageAnswers: false,
          mcAnswerImageAssetsBasePath: '',
          geniusImageAssetsBasePath: '',
          geniusSoundAssetsBasePath: '',
          tileMatchImageAssetsBasePath: '',
          recommendedSolo: false,
          isUnlockable: false,
          characterUnlockEnabled: false,
          supportsPlayDifficultyEasy: true,
          supportsPlayDifficultyMedium: true,
          supportsPlayDifficultyHard: true,
        });
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Quiz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Quiz type</Form.Label>
              <Form.Select
                value={formData.format}
                onChange={(e) =>
                  setFormData((prev) =>
                    applyQuizFormatChange(
                      prev,
                      e.target.value as QuizFormData['format']
                    )
                  )
                }
              >
                <option value="MultipleChoice">Multiple choice</option>
                <option value="Witc">WITC — Who Is This Character (imagem + texto)</option>
                <option value="Stop">Stop — letra + respostas com imagem (solo, médio)</option>
                <option value="Genius">Genius — sequência de imagens e sons (solo, médio)</option>
                <option value="TileMatch">Junta-Peças / Tile Match (solo, médio)</option>
                <option value="WordConnect">Caça-Palavras / Word Connect (solo)</option>
              </Form.Select>
            </Form.Group>

            <>
              <Form.Group className="mb-3">
                <Form.Label>Título (português) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nome do quiz em português"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title (English) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.titleEn}
                  onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                  placeholder="Quiz title in English"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tema (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder="Segunda linha no lobby (PT)"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Theme / subtitle (English, optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.themeEn}
                  onChange={(e) => setFormData({ ...formData, themeEn: e.target.value })}
                  placeholder="Second lobby line in English"
                />
              </Form.Group>
            </>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Difficulty *</Form.Label>
                  <Form.Select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  {formData.format === 'Genius' ? (
                    <>
                      <Form.Label>{t('admin.geniusPoolMinLabel', 'Itens no pool (mínimo)')}</Form.Label>
                      <Form.Control type="text" value={String(GENIUS_MIN_POOL_ITEMS)} readOnly disabled />
                      <Form.Text className="text-muted">
                        {t(
                          'admin.geniusPoolMinHelp',
                          'Genius usa 4 imagens/sons por partida; as 10 rodadas são geradas ao jogar. Cadastre pelo menos 4 itens no pool.'
                        )}
                      </Form.Text>
                    </>
                  ) : (
                    <>
                      <Form.Label>Question Count *</Form.Label>
                      <Form.Control
                        type="number"
                        min={10}
                        max={100}
                        value={formData.questionCount}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setFormData({ ...formData, questionCount: value });
                          if (value >= 10 && value <= 100) {
                            setError(null);
                          }
                        }}
                        required
                      />
                      {formData.questionCount < 10 ? (
                        <Form.Text className="text-danger">
                          Question count must be at least 10
                        </Form.Text>
                      ) : formData.questionCount > 100 ? (
                        <Form.Text className="text-danger">
                          Question count must be at most 100
                        </Form.Text>
                      ) : (
                        <Form.Text className="text-muted">Between 10 and 100 questions</Form.Text>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {isBilingualLobbyFormat(formData.format) && (
              <Alert variant="secondary" className="py-2 small">
                Quizzes de múltipla escolha e WITC usam português e inglês no lobby e nas perguntas; o idioma do
                jogador é escolhido na aplicação.
              </Alert>
            )}

            {isBilingualLobbyFormat(formData.format) && formData.format !== 'Stop' && (
              <Form.Group className="mb-3">
                <Form.Label>Modos de dificuldade (cadastro de respostas)</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="checkbox"
                    id="create-pd-easy"
                    checked={formData.supportsPlayDifficultyEasy}
                    onChange={(e) =>
                      setFormData({ ...formData, supportsPlayDifficultyEasy: e.target.checked })
                    }
                    label="Fácil"
                  />
                  <Form.Check
                    type="checkbox"
                    id="create-pd-medium"
                    checked={formData.supportsPlayDifficultyMedium}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        supportsPlayDifficultyMedium: v,
                        ...(!v ? { supportsPlayDifficultyHard: false } : {}),
                      }));
                    }}
                    label="Médio (obrigatório se marcar difícil — o difícil reutiliza as respostas do médio)"
                  />
                  <Form.Check
                    type="checkbox"
                    id="create-pd-hard"
                    checked={formData.supportsPlayDifficultyHard}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        supportsPlayDifficultyHard: v,
                        ...(v ? { supportsPlayDifficultyMedium: true } : {}),
                      }));
                    }}
                    label="Difícil (no jogador, mesma resposta que o médio)"
                  />
                </div>
                {!playDifficultyOk(formData) && (
                  <Form.Text className="text-danger d-block mt-1">
                    Marque pelo menos um modo. Se marcar difícil, marque também médio.
                  </Form.Text>
                )}
              </Form.Group>
            )}

            {formData.format === 'TileMatch' && (
              <Form.Group className="mb-3">
                <Form.Label>Pasta de imagens Tile Match (public/assets/images/) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tileMatchImageAssetsBasePath}
                  onChange={(e) =>
                    setFormData({ ...formData, tileMatchImageAssetsBasePath: e.target.value })
                  }
                  placeholder="ex.: tile-match-set"
                />
                <Form.Text className="text-muted">
                  Cadastre pelo menos 11 itens no pool com ImageAssetKey. 10 rodadas geradas ao jogar.
                </Form.Text>
              </Form.Group>
            )}

            {formData.format === 'Genius' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta de imagens Genius (public/assets/images/) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.geniusImageAssetsBasePath}
                    onChange={(e) =>
                      setFormData({ ...formData, geniusImageAssetsBasePath: e.target.value })
                    }
                    placeholder="ex.: genius-pokemon"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta de sons Genius (public/assets/sounds/) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.geniusSoundAssetsBasePath}
                    onChange={(e) =>
                      setFormData({ ...formData, geniusSoundAssetsBasePath: e.target.value })
                    }
                    placeholder="ex.: genius-pokemon"
                  />
                  <Form.Text className="text-muted">
                    Cadastre itens no pool com ImageAssetKey e SoundAssetKey (mín. 4). Sons em{' '}
                    <code>.mp3</code> {t('admin.geniusOr', 'ou')} <code>.wav</code>.
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {usesWitcImageAssetsFolder(formData.format) && (
              <Form.Group className="mb-3">
                <Form.Label>Pasta de imagens (em public/assets/images/) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.witcImageAssetsBasePath}
                  onChange={(e) => setFormData({ ...formData, witcImageAssetsBasePath: e.target.value })}
                  placeholder="ex.: pokemon-gen1"
                />
                <Form.Text className="text-muted">
                  Coloque WebP ou PNG em <code>public/assets/images/{'{nome}'}/</code>
                  {formData.format === 'Stop'
                    ? ' — uma imagem por resposta aceite no cadastro de perguntas.'
                    : ' — uma chave por pergunta no cadastro.'}
                </Form.Text>
              </Form.Group>
            )}

            {formData.format === 'MultipleChoice' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta raiz das imagens das perguntas (opcional, public/assets/images/)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mcImageAssetsBasePath}
                    onChange={(e) => setFormData({ ...formData, mcImageAssetsBasePath: e.target.value })}
                    placeholder="ex.: meu-quiz-imagens"
                  />
                  <Form.Text className="text-muted">
                    Imagem grande acima das opções (opcional). WebP ou PNG em{' '}
                    <code>public/assets/images/{'{nome}'}/</code> — chave por pergunta no cadastro.
                  </Form.Text>
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  id="create-mc-use-image-answers"
                  className="mb-2"
                  label="Utilizar imagens como perguntas?"
                  checked={formData.mcUseImageAnswers}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      mcUseImageAnswers: e.target.checked,
                      ...(!e.target.checked ? { mcAnswerImageAssetsBasePath: '' } : {}),
                    })
                  }
                />
                {formData.mcUseImageAnswers && (
                  <Form.Group className="mb-3">
                    <Form.Label>Pasta das imagens das opções (public/assets/) *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.mcAnswerImageAssetsBasePath}
                      onChange={(e) =>
                        setFormData({ ...formData, mcAnswerImageAssetsBasePath: e.target.value })
                      }
                      placeholder="ex.: pokemon types"
                      required
                    />
                    <Form.Text className="text-muted">
                      Coloque WebP ou PNG em{' '}
                      <code>public/assets/{'{pasta}'}/</code> — informe o nome do ficheiro em cada opção no cadastro
                      de perguntas. Sem chave na opção, mantém-se o texto.
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Imagem de capa no lobby de quizzes (opcional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.imageUrl}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({
                    ...formData,
                    imageUrl: /^https?:\/\//i.test(v) ? v : v.replace(/[^a-zA-Z0-9_-]/g, ''),
                  });
                }}
                placeholder="ex.: kanto-banner"
              />
              <Form.Text className="text-muted">
                Nome do arquivo sem extensão; use <code>.webp</code> ou <code>.png</code> em{' '}
                <code>public/assets/preview/</code> (igual às imagens das perguntas WITC, pasta fixa{' '}
                <code>preview</code>). URLs antigas ainda podem ser editadas se começarem com{' '}
                <code>http(s)://</code>.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (optional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="e.g. Weaknesses, Kanto"
              />
              <Form.Text className="text-muted">
                Comma-separated. Applied to the whole quiz for achievements (e.g. TOTAL_CORRECT_ANSWERS_BY_TAG).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="admin-create-recommended-solo"
                checked={formData.recommendedSolo}
                onChange={(e) => setFormData({ ...formData, recommendedSolo: e.target.checked })}
                label={t('admin.soloRecommendedLabel', 'Recommended for quiz play (popular — appears at top of the list)')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="admin-create-unlockable"
                checked={formData.isUnlockable}
                onChange={(e) => setFormData({ ...formData, isUnlockable: e.target.checked })}
                label={t(
                  'admin.quizUnlockableLabel',
                  'Unlockable (show lock in lobby; only these quizzes can be career mission unlock rewards)'
                )}
              />
            </Form.Group>
            {isBilingualLobbyFormat(formData.format) && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="admin-create-character-unlock"
                  checked={formData.characterUnlockEnabled}
                  onChange={(e) =>
                    setFormData({ ...formData, characterUnlockEnabled: e.target.checked })
                  }
                  label={t(
                    'admin.quizCharacterUnlockLabel',
                    'Character unlock chain (optional prerequisite question per question in this quiz)'
                  )}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateQuiz}
            disabled={isQuizFormSaveDisabled(formData)}
          >
            Create Quiz
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Quiz Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Quiz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Quiz type</Form.Label>
              <Form.Select
                value={formData.format}
                onChange={(e) =>
                  setFormData((prev) =>
                    applyQuizFormatChange(
                      prev,
                      e.target.value as QuizFormData['format']
                    )
                  )
                }
                disabled={!!selectedQuiz && (selectedQuiz.totalQuestions ?? 0) > 0}
              >
                <option value="MultipleChoice">Multiple choice</option>
                <option value="Witc">WITC — Who Is This Character</option>
                <option value="Stop">Stop — letra + imagens (solo, médio)</option>
                <option value="Genius">Genius — sequência imagem/som (solo, médio)</option>
                <option value="TileMatch">Junta-Peças / Tile Match (solo, médio)</option>
                <option value="WordConnect">Caça-Palavras / Word Connect (solo)</option>
              </Form.Select>
              {!!selectedQuiz && selectedQuiz.totalQuestions > 0 && (
                <Form.Text className="text-muted">Tipo fixo enquanto existirem perguntas.</Form.Text>
              )}
            </Form.Group>

            <>
              <Form.Group className="mb-3">
                <Form.Label>Título (português) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Nome do quiz em português"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title (English) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.titleEn}
                  onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                  placeholder="Quiz title in English"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tema (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder="Segunda linha no lobby (PT)"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Theme / subtitle (English, optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.themeEn}
                  onChange={(e) => setFormData({ ...formData, themeEn: e.target.value })}
                  placeholder="Second lobby line in English"
                />
              </Form.Group>
            </>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Difficulty *</Form.Label>
                  <Form.Select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  {formData.format === 'Genius' ? (
                    <>
                      <Form.Label>{t('admin.geniusPoolMinLabel', 'Itens no pool (mínimo)')}</Form.Label>
                      <Form.Control type="text" value={String(GENIUS_MIN_POOL_ITEMS)} readOnly disabled />
                      <Form.Text className="text-muted">
                        {t(
                          'admin.geniusPoolMinHelp',
                          'Genius usa 4 imagens/sons por partida; as 10 rodadas são geradas ao jogar. Cadastre pelo menos 4 itens no pool.'
                        )}
                      </Form.Text>
                    </>
                  ) : (
                    <>
                      <Form.Label>Question Count *</Form.Label>
                      <Form.Control
                        type="number"
                        min={10}
                        max={100}
                        value={formData.questionCount}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setFormData({ ...formData, questionCount: value });
                          if (value >= 10 && value <= 100) {
                            setError(null);
                          }
                        }}
                        required
                      />
                      {formData.questionCount < 10 ? (
                        <Form.Text className="text-danger">
                          Question count must be at least 10
                        </Form.Text>
                      ) : formData.questionCount > 100 ? (
                        <Form.Text className="text-danger">
                          Question count must be at most 100
                        </Form.Text>
                      ) : (
                        <Form.Text className="text-muted">Between 10 and 100 questions</Form.Text>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {isBilingualLobbyFormat(formData.format) && (
              <Alert variant="secondary" className="py-2 small">
                Quizzes de múltipla escolha e WITC usam português e inglês no lobby e nas perguntas; o idioma do
                jogador é escolhido na aplicação.
              </Alert>
            )}

            {isBilingualLobbyFormat(formData.format) && formData.format !== 'Stop' && (
              <Form.Group className="mb-3">
                <Form.Label>Modos de dificuldade (cadastro de respostas)</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="checkbox"
                    id="edit-pd-easy"
                    checked={formData.supportsPlayDifficultyEasy}
                    onChange={(e) =>
                      setFormData({ ...formData, supportsPlayDifficultyEasy: e.target.checked })
                    }
                    label="Fácil"
                  />
                  <Form.Check
                    type="checkbox"
                    id="edit-pd-medium"
                    checked={formData.supportsPlayDifficultyMedium}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        supportsPlayDifficultyMedium: v,
                        ...(!v ? { supportsPlayDifficultyHard: false } : {}),
                      }));
                    }}
                    label="Médio (obrigatório se marcar difícil — o difícil reutiliza as respostas do médio)"
                  />
                  <Form.Check
                    type="checkbox"
                    id="edit-pd-hard"
                    checked={formData.supportsPlayDifficultyHard}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        supportsPlayDifficultyHard: v,
                        ...(v ? { supportsPlayDifficultyMedium: true } : {}),
                      }));
                    }}
                    label="Difícil (no jogador, mesma resposta que o médio)"
                  />
                </div>
                {!playDifficultyOk(formData) && (
                  <Form.Text className="text-danger d-block mt-1">
                    Marque pelo menos um modo. Se marcar difícil, marque também médio.
                  </Form.Text>
                )}
              </Form.Group>
            )}

            {formData.format === 'TileMatch' && (
              <Form.Group className="mb-3">
                <Form.Label>Pasta de imagens Tile Match (public/assets/images/) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tileMatchImageAssetsBasePath}
                  onChange={(e) =>
                    setFormData({ ...formData, tileMatchImageAssetsBasePath: e.target.value })
                  }
                  placeholder="ex.: tile-match-set"
                />
                <Form.Text className="text-muted">
                  Cadastre pelo menos 11 itens no pool com ImageAssetKey. 10 rodadas geradas ao jogar.
                </Form.Text>
              </Form.Group>
            )}

            {formData.format === 'Genius' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta de imagens Genius (public/assets/images/) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.geniusImageAssetsBasePath}
                    onChange={(e) =>
                      setFormData({ ...formData, geniusImageAssetsBasePath: e.target.value })
                    }
                    placeholder="ex.: genius-pokemon"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta de sons Genius (public/assets/sounds/) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.geniusSoundAssetsBasePath}
                    onChange={(e) =>
                      setFormData({ ...formData, geniusSoundAssetsBasePath: e.target.value })
                    }
                    placeholder="ex.: genius-pokemon"
                  />
                  <Form.Text className="text-muted">
                    Cadastre itens no pool com ImageAssetKey e SoundAssetKey (mín. 4). Sons em{' '}
                    <code>.mp3</code> {t('admin.geniusOr', 'ou')} <code>.wav</code>.
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {usesWitcImageAssetsFolder(formData.format) && (
              <Form.Group className="mb-3">
                <Form.Label>Pasta de imagens (public/assets/images/) *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.witcImageAssetsBasePath}
                  onChange={(e) => setFormData({ ...formData, witcImageAssetsBasePath: e.target.value })}
                  placeholder="ex.: pokemon-gen1"
                />
              </Form.Group>
            )}

            {formData.format === 'MultipleChoice' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Pasta raiz das imagens das perguntas (opcional, public/assets/images/)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mcImageAssetsBasePath}
                    onChange={(e) => setFormData({ ...formData, mcImageAssetsBasePath: e.target.value })}
                    placeholder="ex.: meu-quiz-imagens"
                  />
                  <Form.Text className="text-muted">
                    Imagem grande acima das opções (opcional). Obrigatório se alguma pergunta tiver{' '}
                    <code>imageAssetKey</code> de pergunta no JSON ou no formulário.
                  </Form.Text>
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  id="edit-mc-use-image-answers"
                  className="mb-2"
                  label="Utilizar imagens como perguntas?"
                  checked={formData.mcUseImageAnswers}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      mcUseImageAnswers: e.target.checked,
                      ...(!e.target.checked ? { mcAnswerImageAssetsBasePath: '' } : {}),
                    })
                  }
                />
                {formData.mcUseImageAnswers && (
                  <Form.Group className="mb-3">
                    <Form.Label>Pasta das imagens das opções (public/assets/) *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.mcAnswerImageAssetsBasePath}
                      onChange={(e) =>
                        setFormData({ ...formData, mcAnswerImageAssetsBasePath: e.target.value })
                      }
                      placeholder="ex.: pokemon types"
                      required
                    />
                    <Form.Text className="text-muted">
                      WebP ou PNG em <code>public/assets/{'{pasta}'}/</code> — chave por opção no cadastro de
                      perguntas. Sem chave, mantém-se o texto.
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Imagem de capa no lobby de quizzes (opcional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.imageUrl}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({
                    ...formData,
                    imageUrl: /^https?:\/\//i.test(v) ? v : v.replace(/[^a-zA-Z0-9_-]/g, ''),
                  });
                }}
                placeholder="ex.: kanto-banner"
              />
              <Form.Text className="text-muted">
                Nome do arquivo sem extensão; <code>.webp</code> ou <code>.png</code> em{' '}
                <code>public/assets/preview/</code>. URLs antigas: campo pode começar com{' '}
                <code>http(s)://</code>.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (optional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="e.g. Weaknesses, Kanto"
              />
              <Form.Text className="text-muted">
                Comma-separated. Save with empty field to clear all quiz tags.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="admin-edit-recommended-solo"
                checked={formData.recommendedSolo}
                onChange={(e) => setFormData({ ...formData, recommendedSolo: e.target.checked })}
                label={t('admin.soloRecommendedLabel', 'Recommended for quiz play (popular — appears at top of the list)')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="admin-edit-unlockable"
                checked={formData.isUnlockable}
                onChange={(e) => setFormData({ ...formData, isUnlockable: e.target.checked })}
                label={t(
                  'admin.quizUnlockableLabel',
                  'Unlockable (show lock in lobby; only these quizzes can be career mission unlock rewards)'
                )}
              />
            </Form.Group>
            {isBilingualLobbyFormat(formData.format) && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="admin-edit-character-unlock"
                  checked={formData.characterUnlockEnabled}
                  onChange={(e) =>
                    setFormData({ ...formData, characterUnlockEnabled: e.target.checked })
                  }
                  label={t(
                    'admin.quizCharacterUnlockLabel',
                    'Character unlock chain (optional prerequisite question per question in this quiz)'
                  )}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditQuiz}
            disabled={isQuizFormSaveDisabled(formData)}
          >
            Update Quiz
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Active Quiz Confirmation Modal */}
      <Modal show={showActiveQuizModal} onHide={() => setShowActiveQuizModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Quiz is Active</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3">"{selectedQuiz?.title}" is currently active</h5>
            <p className="text-muted">
              Active quizzes cannot be edited directly. To edit this quiz, it needs to be set back to draft mode first.
            </p>
            <p className="text-muted">
              <strong>Note:</strong> Setting the quiz to draft will make it unavailable for users until it's activated again.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActiveQuizModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleConfirmEditActiveQuiz}
          >
            <i className="bi bi-pencil me-2"></i>
            Set to Draft & Edit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Quiz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{selectedQuiz?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteQuiz}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminQuizList; 